import { useCallback, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import EditIcon from '@mui/icons-material/Edit';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { Avatar, Button } from '@bloomays-lib/ui.shared';
import { useTranslation } from 'react-i18next';
import { MonthlyPrimeHeaderAmounts } from '../../types/primes';
import { formatPrimeEuro, formatPrimeEuroWithSign, formatPrimeValidationDate } from '../../utils/monthlyPrime.utils';
import PrimeValueKpi from './PrimeValueKpi';

export type MonthlyPrimeHeaderProps = {
  tamDisplayName: string;
  tamEmail: string;
  tamSeniority?: string;
  tamAvatarUrl?: string | null;
  amounts?: MonthlyPrimeHeaderAmounts;
  loading: boolean;
  isValidated?: boolean;
  validationDate?: Date | string;
  canValidate?: boolean;
  isValidating?: boolean;
  validateDisabledTooltip?: string;
  onValidate?: () => void;
  canAddRegularisation?: boolean;
  hasExistingRegularisation?: boolean;
  onAddRegularisation?: () => void;
  showValidateButton?: boolean;
};

const MonthlyPrimeHeader = ({
  tamDisplayName,
  tamEmail,
  tamSeniority,
  tamAvatarUrl,
  amounts,
  loading,
  isValidated = false,
  validationDate,
  canValidate = false,
  isValidating = false,
  validateDisabledTooltip,
  onValidate,
  canAddRegularisation = false,
  hasExistingRegularisation = false,
  onAddRegularisation,
  showValidateButton = true,
}: MonthlyPrimeHeaderProps) => {
  const { t } = useTranslation(['primes']);
  const [reasonPopoverPosition, setReasonPopoverPosition] = useState<{ top: number; left: number } | null>(null);

  const formatAmount = (value: number | undefined) => (loading ? '...' : formatPrimeEuro(value ?? 0));

  const formatRegularisation = (value: number | undefined) => (loading ? '...' : formatPrimeEuroWithSign(value ?? 0));

  const regularisationValue = amounts?.regularisation ?? 0;
  const regularisationAccent =
    !loading && regularisationValue < 0 ? 'negative' : !loading && regularisationValue > 0 ? 'green' : 'default';

  const seniorityLabel = tamSeniority?.trim() || '—';
  const showRegularisationAction = !isValidated && canAddRegularisation;
  const consultableReason = amounts?.regularisationReason?.trim() ?? '';
  const showReasonConsultation = !canAddRegularisation && consultableReason.length > 0;

  const handleReasonBadgeClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setReasonPopoverPosition({
      top: rect.bottom + 8,
      left: Math.max(16, rect.left + rect.width / 2 - 160),
    });
  }, []);

  const handleReasonPopoverClose = useCallback(() => {
    setReasonPopoverPosition(null);
  }, []);

  const validateButton = (
    <Button
      disable={!canValidate || isValidating}
      loading={isValidating}
      textButton={t('validateMonth')}
      emoji={<CheckIcon />}
      buttonType="button"
      size="large"
      onClick={onValidate}
    />
  );

  return (
    <HeaderShell>
      <HeaderContainer>
        <ProfileRow>
          <ProfileIdentity>
            <ProfileAvatar>
              <Avatar size="medium" alt={tamDisplayName || '—'} image={tamAvatarUrl} />
            </ProfileAvatar>
            <IdentityTextBlock>
              <NameRow>
                <TamName>{tamDisplayName || '—'}</TamName>
                <SeniorityBadge title={t('seniorityLabel')}>
                  <WorkspacePremiumIcon sx={{ fontSize: 13 }} />
                  {seniorityLabel}
                </SeniorityBadge>
              </NameRow>
              <TamEmail>{tamEmail || '—'}</TamEmail>
            </IdentityTextBlock>
          </ProfileIdentity>

          <ProfileActions>
            {isValidated ? (
              <StatusPill $tone="validated">
                <CheckIcon sx={{ fontSize: 13 }} />
                {t('monthStatusValidated')}
              </StatusPill>
            ) : (
              <StatusPill $tone="pending">{t('monthStatusPending')}</StatusPill>
            )}
            {isValidated && validationDate ? (
              <ValidationDateText>
                {t('validationDateLabel', {
                  date: formatPrimeValidationDate(validationDate),
                })}
              </ValidationDateText>
            ) : null}
            {!isValidated && showValidateButton ? (
              validateDisabledTooltip ? (
                <Tooltip title={validateDisabledTooltip} arrow>
                  <ValidateButtonWrapper>{validateButton}</ValidateButtonWrapper>
                </Tooltip>
              ) : (
                validateButton
              )
            ) : null}
          </ProfileActions>
        </ProfileRow>

        <KpiStrip>
          <PrimeValueKpi
            title={t('headerFreelanceMarge')}
            value={formatAmount(amounts?.freelanceMarge)}
            loading={loading}
          />

          <KpiDivider />

          <PrimeValueKpi
            title={t('headerBillingMarge')}
            value={formatAmount(amounts?.billingMarge)}
            loading={loading}
          />

          <KpiDivider />

          <PrimeValueKpi
            title={t('headerCommission')}
            value={formatAmount(amounts?.commission)}
            loading={loading}
            variant="green"
          />

          <KpiDivider />

          <PrimeValueKpi title={t('headerBonus')} value={formatAmount(amounts?.bonus)} loading={loading} />

          <KpiDivider />

          <PrimeValueKpi
            title={t('headerRegul')}
            value={formatRegularisation(amounts?.regularisation)}
            loading={loading}
            variant={regularisationAccent}
            titleAdornment={
              showRegularisationAction ? (
                <RegulTitleIcon aria-hidden="true">
                  <EditIcon sx={{ fontSize: 14 }} />
                </RegulTitleIcon>
              ) : showReasonConsultation ? (
                <RegulCommentTrigger
                  type="button"
                  aria-label={t('regularisationPopoverOpen')}
                  onClick={handleReasonBadgeClick}
                >
                  <ChatBubbleOutlineIcon sx={{ fontSize: 12 }} />
                </RegulCommentTrigger>
              ) : undefined
            }
            onClick={
              showRegularisationAction
                ? () => {
                    onAddRegularisation?.();
                  }
                : undefined
            }
            ariaLabel={
              showRegularisationAction
                ? t(hasExistingRegularisation ? 'editRegularisation' : 'addRegularisation')
                : undefined
            }
          />

          <Popover
            open={reasonPopoverPosition !== null}
            onClose={handleReasonPopoverClose}
            anchorReference="anchorPosition"
            anchorPosition={reasonPopoverPosition ?? undefined}
            TransitionProps={{ unmountOnExit: true }}
            slotProps={{
              paper: {
                sx: { position: 'fixed', overflow: 'visible' },
              },
            }}
          >
            <ReasonPopoverCard>
              <ReasonPopoverHeader>
                <ReasonPopoverTitle>{t('regularisationPopoverTitle')}</ReasonPopoverTitle>
                <ReasonPopoverCloseButton
                  type="button"
                  aria-label={t('regularisationPopoverClose')}
                  onClick={handleReasonPopoverClose}
                >
                  ×
                </ReasonPopoverCloseButton>
              </ReasonPopoverHeader>
              <ReasonPopoverBody>{consultableReason}</ReasonPopoverBody>
            </ReasonPopoverCard>
          </Popover>

          <PrimeValueKpi
            title={t('headerTotalToReceive')}
            value={formatAmount(amounts?.total)}
            variant="highlight"
            loading={loading}
          />
        </KpiStrip>
      </HeaderContainer>
    </HeaderShell>
  );
};

export default MonthlyPrimeHeader;

const HeaderShell = styled('div')(() => ({
  borderRadius: '18px',
  boxShadow: '0 8px 24px rgba(75, 75, 75, 0.12)',
  marginTop: 0,
}));

const HeaderContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.neutral.white,
  borderRadius: '18px',
  overflow: 'hidden',
  textAlign: 'left',
}));

const ProfileRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2.5),
  flexWrap: 'wrap',
  padding: '17px 24px',
}));

const ProfileIdentity = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '13px',
  minWidth: 0,
  flex: '1 1 280px',
}));

const ProfileAvatar = styled('div')(() => ({
  flexShrink: 0,
  '& .MuiAvatar-root': {
    width: 46,
    height: 46,
    fontSize: 15,
  },
}));

const IdentityTextBlock = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
  minWidth: 0,
}));

const NameRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '9px',
}));

const TamName = styled('span')(({ theme }) => ({
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: '15.5px',
  fontWeight: 600,
  color: theme.palette.neutral[90],
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 280,
}));

const SeniorityBadge = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  padding: '3px 10px 3px 8px',
  borderRadius: 999,
  backgroundColor: theme.palette.purple[20],
  color: theme.palette.purple[100],
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: '11.5px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  flexShrink: 0,
}));

const TamEmail = styled('span')(({ theme }) => ({
  fontFamily: '"Poppins Regular", sans-serif',
  fontSize: '13px',
  color: theme.palette.neutral[60],
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 320,
}));

const ProfileActions = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.75),
  flexWrap: 'wrap',
  flexShrink: 0,
}));

const StatusPill = styled('span', {
  shouldForwardProp: (prop) => prop !== '$tone',
})<{ $tone: 'pending' | 'validated' }>(({ theme, $tone }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 13px',
  borderRadius: 999,
  fontFamily: '"Poppins Medium", sans-serif',
  fontSize: '12.5px',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  ...($tone === 'validated'
    ? {
        backgroundColor: theme.palette.green[20],
        color: theme.palette.green[100],
      }
    : {
        backgroundColor: theme.palette.beige[40],
        color: theme.palette.beige[100],
        '&::before': {
          content: '"•"',
          marginRight: '2px',
        },
      }),
}));

const ValidationDateText = styled('span')(({ theme }) => ({
  fontFamily: '"Poppins Regular", sans-serif',
  fontSize: '12.5px',
  color: theme.palette.neutral[60],
  whiteSpace: 'nowrap',
}));

const ValidateButtonWrapper = styled('span')(() => ({
  display: 'inline-flex',
}));

const KpiStrip = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'stretch',
  borderTop: `1px solid ${theme.palette.neutral[15]}`,
  backgroundColor: theme.palette.neutral[10],
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const KpiDivider = styled('span')(({ theme }) => ({
  width: 1,
  backgroundColor: theme.palette.neutral[20],
  margin: '18px 0',
  flexShrink: 0,
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const RegulTitleIcon = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  color: theme.palette.red[80],
  flexShrink: 0,
}));

const RegulCommentTrigger = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 22,
  height: 22,
  padding: 0,
  border: 'none',
  borderRadius: '50%',
  backgroundColor: theme.palette.red[80],
  color: theme.palette.neutral.white,
  flexShrink: 0,
  cursor: 'pointer',
  transition: 'background-color 0.15s ease',
  '&:hover': {
    backgroundColor: theme.palette.red[100],
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.red[40]}`,
    outlineOffset: 2,
  },
}));

const ReasonPopoverCard = styled('div')(({ theme }) => ({
  width: 320,
  padding: '14px 16px 16px',
  borderRadius: 14,
  backgroundColor: theme.palette.neutral.white,
  boxShadow: '0 10px 28px rgba(33, 33, 33, 0.14)',
  // body { text-align: center } in main.css — inline text would inherit it otherwise.
  textAlign: 'left',
}));

const ReasonPopoverHeader = styled('div')(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 10,
}));

const ReasonPopoverTitle = styled('div')(({ theme }) => ({
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: '10.5px',
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: theme.palette.neutral[50],
  lineHeight: 1.3,
}));

const ReasonPopoverCloseButton = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 22,
  height: 22,
  margin: '-2px -4px 0 0',
  padding: 0,
  border: 'none',
  borderRadius: 6,
  backgroundColor: 'transparent',
  color: theme.palette.neutral[50],
  fontFamily: '"Poppins Regular", sans-serif',
  fontSize: '18px',
  lineHeight: 1,
  cursor: 'pointer',
  '&:hover': {
    color: theme.palette.neutral[70],
    backgroundColor: theme.palette.neutral[10],
  },
}));

const ReasonPopoverBody = styled('div')(({ theme }) => ({
  fontFamily: '"Poppins Regular", sans-serif',
  fontSize: '13.5px',
  color: theme.palette.neutral[90],
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
}));
